import { type MaybeElementRef, unrefElement, useEventListener } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';

export type UseFocusTrapOptions = {
    /** The trap holds focus only while this is `true` */
    active: MaybeRefOrGetter<boolean>;
    /** The element focus stays inside */
    container: MaybeElementRef;
    /** Elements outside the container that may hold focus without it being pulled back: the trigger, so a click on it can close the layer */
    allowed?: MaybeRefOrGetter<readonly MaybeElementRef[]>;
};

const TABBABLE = [
    'a[href]',
    'area[href]',
    'button:not(:disabled)',
    'input:not(:disabled):not([type="hidden"])',
    'select:not(:disabled)',
    'textarea:not(:disabled)',
    'iframe',
    'audio[controls]',
    'video[controls]',
    'summary',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]',
].join(',');

const isVisible = (element: HTMLElement) =>
    !element.closest('[hidden], [inert]') && (typeof element.checkVisibility !== 'function' || element.checkVisibility());

/** Elements inside `container` that Tab reaches, in DOM order. Positive `tabindex` values are not reordered. */
export const getTabbables = (container: HTMLElement): HTMLElement[] =>
    Array.from(container.querySelectorAll<HTMLElement>(TABBABLE)).filter((element) => element.tabIndex >= 0 && isVisible(element));

type Trap = { container: () => HTMLElement | null };

/* Active traps, oldest first. Only the topmost one acts, so a dialog opened from a popover takes focus over. */
const traps: Trap[] = [];

/**
 * Keeps keyboard focus inside a container while it is active: Tab and Shift+Tab wrap around,
 * and focus that lands outside (a script, a click on a focusable element) moves back in, unless it lands on an `allowed` element.
 *
 * On activation it focuses the first `[autofocus]` element inside, else the first tabbable element,
 * else the container itself (give it `tabindex="-1"`). Returning focus on close is the caller's job:
 * only the caller knows whether the close came from Escape or from a click elsewhere.
 *
 * @example
 * useFocusTrap({ active: open, container: panel });
 */
export function useFocusTrap(options: UseFocusTrapOptions): void {
    const container = () => {
        const element = unrefElement(options.container);
        return element instanceof HTMLElement ? element : null;
    };
    const trap: Trap = { container };

    const remove = () => {
        const index = traps.indexOf(trap);
        if (index !== -1) traps.splice(index, 1);
    };
    const isHolding = () => toValue(options.active) && traps.at(-1) === trap && !!container();

    function focusInitial(element: HTMLElement) {
        const target = element.querySelector<HTMLElement>('[autofocus]') ?? getTabbables(element)[0] ?? element;
        target.focus({ preventScroll: true });
    }

    /* `post`, so a container rendered with `v-if` exists before it is focused. */
    watch(
        () => [toValue(options.active), container()] as const,
        ([active, element]) => {
            if (!active || !element) {
                remove();
                return;
            }
            if (traps.includes(trap)) return;
            traps.push(trap);
            if (!element.contains(document.activeElement)) focusInitial(element);
        },
        { immediate: true, flush: 'post' },
    );
    onScopeDispose(remove);

    useEventListener(document, 'keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Tab' || event.defaultPrevented || !isHolding()) return;
        const element = container()!;
        const tabbables = getTabbables(element);
        if (!tabbables.length) {
            event.preventDefault();
            element.focus();
            return;
        }

        const first = tabbables[0];
        const last = tabbables.at(-1)!;
        const current = document.activeElement;
        const isOutside = !(current instanceof Node) || !element.contains(current);
        if (event.shiftKey && (isOutside || current === first || current === element)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (isOutside || current === last)) {
            event.preventDefault();
            first.focus();
        }
    });

    useEventListener(document, 'focusin', (event: FocusEvent) => {
        if (!isHolding() || !(event.target instanceof Node)) return;
        const target = event.target;
        const element = container()!;
        if (element.contains(target)) return;
        if (toValue(options.allowed)?.some((allowed) => unrefElement(allowed)?.contains(target))) return;
        focusInitial(element);
    });
}
